import { useState, useMemo, type ReactNode } from 'react';
import { Layout, Drawer, Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  DashboardOutlined,
  RobotOutlined,
  TeamOutlined,
  BarChartOutlined,
  FileTextOutlined,
  FileSearchOutlined,
  MessageOutlined,
  ApiOutlined,
  SettingOutlined,
  CloseOutlined,
  UserOutlined,
  ThunderboltOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  LogoutOutlined,
  CustomerServiceOutlined,
  ExperimentOutlined,
  BookOutlined,
  PieChartOutlined,
  TagsOutlined,
  BellOutlined,
  CodeOutlined,
  UsergroupAddOutlined,
  MonitorOutlined,
  GoogleOutlined,
} from '@ant-design/icons';
import { useAuth } from '@/hooks/useAuth';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { BRAND_NAME, BRAND_LOGO_URL } from '@/config/constants';
import { useTenantViewStore } from '@/store/tenantView.store';
import { resolveSidebarContext, type SidebarNavContext } from '@/utils/sidebarContext';
import { getBotsListPath } from '@/utils/botsListPath';
import {
  agentWorkspaceHref,
  botAnalyticsHref,
  botReportingHref,
  livechatHref,
} from '@/utils/botScopeLinks';
import { navItemActive } from '@/utils/sidebarNavActive';
import toast from 'react-hot-toast';
import './Sidebar.css';

const { Sider } = Layout;

interface SidebarProps {
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

type NavItem = {
  /** Route key for `menu_access` / MenuAccessRoute (path without query). */
  menuKey: string;
  /** Full navigation target (may include query string). */
  to: string;
  icon: ReactNode;
  label: string;
};

type NavGroup = {
  label?: string;
  items: NavItem[];
};

function filterNavGroupsByAccess(groups: NavGroup[], menuAccess: unknown): NavGroup[] {
  if (!menuAccess || !Array.isArray(menuAccess)) return groups;
  return groups
    .map((g) => ({ ...g, items: g.items.filter((i) => menuAccess.includes(i.menuKey)) }))
    .filter((g) => g.items.length > 0);
}

function buildNavForRole(
  role: string,
  context: SidebarNavContext,
  tenantId: string | null,
  botId: string | null
): NavGroup[] {
  const r = role.toLowerCase();

  if (r === 'superadmin' && context === 'platform') {
    return [
      {
        label: 'Overview',
        items: [{ menuKey: '/dashboard', to: '/dashboard', icon: <DashboardOutlined />, label: 'Dashboard' }],
      },
      {
        label: 'Core',
        items: [
          { menuKey: '/tenants', to: '/tenants', icon: <TeamOutlined />, label: 'Tenants' },
          {
            menuKey: '/platform/users',
            to: '/platform/users',
            icon: <UsergroupAddOutlined />,
            label: 'Platform users',
          },
          { menuKey: '/bots', to: '/bots', icon: <RobotOutlined />, label: 'Bots' },
          { menuKey: '/tickets', to: '/tickets', icon: <TagsOutlined />, label: 'Tickets' },
        ],
      },
      {
        label: 'AI & skills',
        items: [
          { menuKey: '/skills', to: '/skills', icon: <ThunderboltOutlined />, label: 'Skills' },
          { menuKey: '/admin/ai-chats', to: '/admin/ai-chats', icon: <MessageOutlined />, label: 'AI chats' },
          { menuKey: '/admin/ai-learning', to: '/admin/ai-learning', icon: <ExperimentOutlined />, label: 'AI learning' },
        ],
      },
      {
        label: 'Integrations',
        items: [{ menuKey: '/channels', to: '/channels', icon: <ApiOutlined />, label: 'Channels' }],
      },
      {
        label: 'Platform',
        items: [
          { menuKey: '/config', to: '/config', icon: <SettingOutlined />, label: 'Platform configuration' },
          { menuKey: '/announcements', to: '/announcements', icon: <BellOutlined />, label: 'Announcements' },
          { menuKey: '/logs', to: '/logs', icon: <FileTextOutlined />, label: 'Log viewer' },
        ],
      },
      {
        label: 'Insights & docs',
        items: [
          { menuKey: '/analytics', to: '/analytics', icon: <BarChartOutlined />, label: 'Platform analytics' },
          { menuKey: '/monitoring', to: '/monitoring', icon: <MonitorOutlined />, label: 'Monitoring' },
          { menuKey: '/reporting', to: '/reporting', icon: <PieChartOutlined />, label: 'Platform reporting' },
          { menuKey: '/developer-docs', to: '/developer-docs', icon: <CodeOutlined />, label: 'Developer docs' },
        ],
      },
      {
        label: 'Account',
        items: [{ menuKey: '/profile', to: '/profile', icon: <UserOutlined />, label: 'Profile' }],
      },
    ];
  }

  if (r === 'superadmin' && context === 'tenant' && tenantId) {
    const t = tenantId;
    return [
      {
        label: 'Overview',
        items: [
          { menuKey: '/dashboard', to: '/dashboard', icon: <DashboardOutlined />, label: 'Dashboard' },
          { menuKey: '/tenants', to: `/tenants/${t}`, icon: <TeamOutlined />, label: 'Home' },
        ],
      },
      {
        label: 'Tenant',
        items: [
          { menuKey: '/bots', to: getBotsListPath(t, 'superadmin'), icon: <RobotOutlined />, label: 'Bots' },
          { menuKey: '/users', to: `/tenants/${t}/users`, icon: <UserOutlined />, label: 'Users' },
          { menuKey: '/integrations/google', to: `/integrations/google?tenantId=${t}`, icon: <GoogleOutlined />, label: 'Google connections' },
          {
            menuKey: '/dashboard/workspace-settings',
            to: `/tenants/${t}/workspace-settings`,
            icon: <SettingOutlined />,
            label: 'Live Chat Settings',
          },
          {
            menuKey: '/dashboard/ai-settings',
            to: `/tenants/${t}/ai-settings`,
            icon: <RobotOutlined />,
            label: 'AI Settings',
          },
          { menuKey: '/reporting', to: `/tenants/${t}/reporting`, icon: <PieChartOutlined />, label: 'Reporting' },
        ],
      },
      {
        label: 'Conversations',
        items: [
          { menuKey: '/internal-chat-logs', to: '/internal-chat-logs', icon: <MessageOutlined />, label: 'Internal chat logs' },
          { menuKey: '/agent-analytics', to: `/tenants/${t}/agent-analytics`, icon: <TeamOutlined />, label: 'Agent analytics' },
        ],
      },
      {
        label: 'Insights',
        items: [
          {
            menuKey: '/analytics',
            to: `/analytics/tenant/${encodeURIComponent(t)}`,
            icon: <BarChartOutlined />,
            label: 'Analytics',
          },
          { menuKey: '/tickets', to: `/tenants/${t}/tickets`, icon: <TagsOutlined />, label: 'Tickets' },
        ],
      },
      { label: 'Help', items: [{ menuKey: '/help', to: '/help', icon: <BookOutlined />, label: 'Help' }] },
    ];
  }

  if (r === 'superadmin' && context === 'bot' && botId) {
    const tid = tenantId;
    const homeTenant = tid ? `/tenants/${tid}` : '/tenants';
    return [
      {
        label: 'Scope',
        items: [
          { menuKey: '/dashboard', to: '/dashboard', icon: <DashboardOutlined />, label: 'Dashboard' },
          { menuKey: '/tenants', to: homeTenant, icon: <TeamOutlined />, label: 'Tenant home' },
        ],
      },
      {
        label: 'Bot',
        items: [
          { menuKey: '/bots', to: `/bots/${botId}/overview`, icon: <RobotOutlined />, label: 'Bot overview' },
          { menuKey: '/bots', to: `/bots/${botId}/config`, icon: <SettingOutlined />, label: 'Bot settings' },
          {
            menuKey: '/analytics',
            to: botAnalyticsHref(botId, tid),
            icon: <BarChartOutlined />,
            label: 'Bot Analytics',
          },
          {
            menuKey: '/reporting',
            to: botReportingHref(botId, tid, 'superadmin'),
            icon: <PieChartOutlined />,
            label: 'Bot Reporting',
          },
        ],
      },
      {
        label: 'Content & actions',
        items: [
          { menuKey: '/knowledge-base', to: '/knowledge-base', icon: <FileSearchOutlined />, label: 'Knowledge base' },
          { menuKey: '/actions', to: '/actions', icon: <ThunderboltOutlined />, label: 'Custom actions' },
          { menuKey: '/integrations/google', to: tid ? `/integrations/google?tenantId=${tid}` : '/integrations/google', icon: <GoogleOutlined />, label: 'Google connections' },
        ],
      },
      {
        label: 'Conversations',
        items: [
          { menuKey: '/livechat', to: livechatHref(botId, tid, 'superadmin'), icon: <MessageOutlined />, label: 'Chat log' },
          {
            menuKey: '/livechat/workspace',
            to: agentWorkspaceHref(botId, tid, 'superadmin'),
            icon: <CustomerServiceOutlined />,
            label: 'Live Chat',
          },
        ],
      },
      { label: 'Help', items: [{ menuKey: '/help', to: '/help', icon: <BookOutlined />, label: 'Help' }] },
    ];
  }

  if (r === 'admin' && context === 'tenant') {
    const ta = tenantId ? `/analytics/tenant/${encodeURIComponent(tenantId)}` : '/analytics';
    return [
      {
        label: 'Overview',
        items: [
          { menuKey: '/dashboard', to: '/dashboard', icon: <DashboardOutlined />, label: 'Home' },
          { menuKey: '/analytics', to: ta, icon: <BarChartOutlined />, label: 'Analytics' },
        ],
      },
      {
        label: 'Build',
        items: [
          { menuKey: '/bots', to: '/bots', icon: <RobotOutlined />, label: 'Bots' },
          { menuKey: '/knowledge-base', to: '/knowledge-base', icon: <FileSearchOutlined />, label: 'Knowledge base' },
          { menuKey: '/actions', to: '/actions', icon: <ThunderboltOutlined />, label: 'Custom actions' },
          { menuKey: '/integrations/google', to: '/integrations/google', icon: <GoogleOutlined />, label: 'Google connections' },
        ],
      },
      {
        label: 'Team & work',
        items: [
          { menuKey: '/users', to: '/users', icon: <UserOutlined />, label: 'Users' },
          { menuKey: '/tickets', to: '/tickets', icon: <TagsOutlined />, label: 'Tickets' },
          { menuKey: '/announcements', to: '/announcements', icon: <BellOutlined />, label: 'Announcements' },
        ],
      },
      {
        label: 'Conversations',
        items: [
          { menuKey: '/internal-chat-logs', to: '/internal-chat-logs', icon: <MessageOutlined />, label: 'Internal chat logs' },
          { menuKey: '/agent-analytics', to: '/agent-analytics', icon: <TeamOutlined />, label: 'Agent analytics' },
        ],
      },
      {
        label: 'Settings',
        items: [
          { menuKey: '/reporting', to: '/reporting', icon: <PieChartOutlined />, label: 'Reporting' },
          {
            menuKey: '/dashboard/workspace-settings',
            to: '/dashboard/workspace-settings',
            icon: <SettingOutlined />,
            label: 'Live Chat Settings',
          },
          {
            menuKey: '/dashboard/ai-settings',
            to: '/dashboard/ai-settings',
            icon: <RobotOutlined />,
            label: 'AI Settings',
          },
        ],
      },
      { label: 'Help', items: [{ menuKey: '/help', to: '/help', icon: <BookOutlined />, label: 'Help' }] },
    ];
  }

  if (r === 'admin' && context === 'bot' && botId) {
    const tid = tenantId;
    return [
      {
        label: 'Scope',
        items: [{ menuKey: '/dashboard', to: '/dashboard', icon: <DashboardOutlined />, label: 'Tenant home' }],
      },
      {
        label: 'Bot',
        items: [
          { menuKey: '/bots', to: `/bots/${botId}/overview`, icon: <RobotOutlined />, label: 'Bot overview' },
          { menuKey: '/bots', to: `/bots/${botId}/config`, icon: <SettingOutlined />, label: 'Bot settings' },
          {
            menuKey: '/analytics',
            to: botAnalyticsHref(botId, tid),
            icon: <BarChartOutlined />,
            label: 'Bot Analytics',
          },
          {
            menuKey: '/reporting',
            to: botReportingHref(botId, tid, 'admin'),
            icon: <PieChartOutlined />,
            label: 'Bot Reporting',
          },
        ],
      },
      {
        label: 'Content & actions',
        items: [
          { menuKey: '/knowledge-base', to: '/knowledge-base', icon: <FileSearchOutlined />, label: 'Knowledge base' },
          { menuKey: '/actions', to: '/actions', icon: <ThunderboltOutlined />, label: 'Custom actions' },
          { menuKey: '/integrations/google', to: '/integrations/google', icon: <GoogleOutlined />, label: 'Google connections' },
        ],
      },
      {
        label: 'Conversations',
        items: [
          { menuKey: '/livechat', to: livechatHref(botId, tid, 'admin'), icon: <MessageOutlined />, label: 'Chat log' },
          {
            menuKey: '/livechat/workspace',
            to: agentWorkspaceHref(botId, tid, 'admin'),
            icon: <CustomerServiceOutlined />,
            label: 'Live Chat',
          },
        ],
      },
      { label: 'Help', items: [{ menuKey: '/help', to: '/help', icon: <BookOutlined />, label: 'Help' }] },
    ];
  }

  if (r === 'creator' && context === 'tenant') {
    const ta = tenantId ? `/analytics/tenant/${encodeURIComponent(tenantId)}` : '/analytics';
    return [
      {
        label: 'Overview',
        items: [
          { menuKey: '/dashboard', to: '/dashboard', icon: <DashboardOutlined />, label: 'Home' },
          { menuKey: '/analytics', to: ta, icon: <BarChartOutlined />, label: 'Analytics' },
        ],
      },
      {
        label: 'Build',
        items: [
          { menuKey: '/bots', to: '/bots', icon: <RobotOutlined />, label: 'Bots' },
          { menuKey: '/knowledge-base', to: '/knowledge-base', icon: <FileSearchOutlined />, label: 'Knowledge base' },
          { menuKey: '/actions', to: '/actions', icon: <ThunderboltOutlined />, label: 'Custom actions' },
          { menuKey: '/integrations/google', to: '/integrations/google', icon: <GoogleOutlined />, label: 'Google connections' },
        ],
      },
      {
        label: 'Account',
        items: [{ menuKey: '/profile', to: '/profile', icon: <UserOutlined />, label: 'Profile' }],
      },
      { label: 'Help', items: [{ menuKey: '/help', to: '/help', icon: <BookOutlined />, label: 'Help' }] },
    ];
  }

  if (r === 'creator' && context === 'bot' && botId) {
    const tid = tenantId;
    return [
      {
        label: 'Scope',
        items: [{ menuKey: '/dashboard', to: '/dashboard', icon: <DashboardOutlined />, label: 'Tenant home' }],
      },
      {
        label: 'Bot',
        items: [
          { menuKey: '/bots', to: `/bots/${botId}/overview`, icon: <RobotOutlined />, label: 'Bot overview' },
          { menuKey: '/bots', to: `/bots/${botId}/config`, icon: <SettingOutlined />, label: 'Bot settings' },
          {
            menuKey: '/analytics',
            to: botAnalyticsHref(botId, tid),
            icon: <BarChartOutlined />,
            label: 'Bot Analytics',
          },
        ],
      },
      {
        label: 'Content & actions',
        items: [
          { menuKey: '/knowledge-base', to: '/knowledge-base', icon: <FileSearchOutlined />, label: 'Knowledge base' },
          { menuKey: '/actions', to: '/actions', icon: <ThunderboltOutlined />, label: 'Custom actions' },
          { menuKey: '/integrations/google', to: '/integrations/google', icon: <GoogleOutlined />, label: 'Google connections' },
        ],
      },
      {
        label: 'Conversations',
        items: [
          { menuKey: '/livechat', to: livechatHref(botId, tid, 'creator'), icon: <MessageOutlined />, label: 'Chat log' },
          {
            menuKey: '/livechat/workspace',
            to: agentWorkspaceHref(botId, tid, 'creator'),
            icon: <CustomerServiceOutlined />,
            label: 'Live Chat',
          },
        ],
      },
      {
        label: 'Account',
        items: [{ menuKey: '/profile', to: '/profile', icon: <UserOutlined />, label: 'Profile' }],
      },
      { label: 'Help', items: [{ menuKey: '/help', to: '/help', icon: <BookOutlined />, label: 'Help' }] },
    ];
  }

  if (r === 'agent' && context === 'tenant') {
    return [
      {
        label: 'Workspace',
        items: [
          { menuKey: '/dashboard', to: '/dashboard', icon: <DashboardOutlined />, label: 'Home' },
          { menuKey: '/livechat', to: '/livechat', icon: <MessageOutlined />, label: 'Chat log' },
          { menuKey: '/livechat/workspace', to: '/livechat/workspace', icon: <CustomerServiceOutlined />, label: 'Live Chat' },
          { menuKey: '/tickets', to: '/tickets', icon: <TagsOutlined />, label: 'Tickets' },
          { menuKey: '/profile', to: '/profile', icon: <UserOutlined />, label: 'Profile' },
        ],
      },
      { label: 'Help', items: [{ menuKey: '/help/agent', to: '/help/agent', icon: <BookOutlined />, label: 'Help' }] },
    ];
  }

  return [{ items: [{ menuKey: '/dashboard', to: '/dashboard', icon: <DashboardOutlined />, label: 'Dashboard' }] }];
}

const Sidebar = ({ mobileOpen = false, onMobileClose }: SidebarProps) => {
  const [collapsed, setCollapsed] = useState(false);
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const selectedTenant = useTenantViewStore((s) => s.selectedTenant);

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logged out successfully');
      navigate('/login');
    } catch {
      toast.error('Logout failed');
    }
  };

  const userMenuItems: MenuProps['items'] = [
    { key: 'profile', icon: <UserOutlined />, label: 'Profile', onClick: () => navigate('/profile') },
    { type: 'divider' },
    { key: 'logout', icon: <LogoutOutlined />, label: 'Logout', danger: true, onClick: handleLogout },
  ];

  const navGroups: NavGroup[] = useMemo(() => {
    const role = user?.role?.toLowerCase() ?? '';
    const menuAccess = user?.menuAccess;

    const { context, tenantId, botId } = resolveSidebarContext({
      pathname: location.pathname,
      search: location.search,
      userRole: user?.role,
      userTenantId: user?.tenantId,
      selectedTenantId: selectedTenant?.id,
    });

    const raw = buildNavForRole(role, context, tenantId, botId);
    return filterNavGroupsByAccess(raw, menuAccess);
  }, [user?.role, user?.menuAccess, user?.tenantId, location.pathname, location.search, selectedTenant?.id]);

  const handleNav = (to: string) => {
    navigate(to);
    if (isMobile && onMobileClose) onMobileClose();
  };

  const renderGroups = (col = false) => (
    <div className="sb-nav">
      {navGroups.map((group, gi) => (
        <div key={gi} className="sb-section">
          {!col && group.label && <div className="sb-group-label">{group.label}</div>}
          {group.items.map((item) => {
            const active = navItemActive(item.to, location.pathname, location.search);
            return (
              <div
                key={`${item.menuKey}-${item.to}`}
                className={`sb-item${active ? ' active' : ''}`}
                onClick={() => handleNav(item.to)}
                role="button"
                tabIndex={0}
                aria-current={active ? 'page' : undefined}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleNav(item.to);
                  }
                }}
                title={col ? item.label : undefined}
              >
                <span className="sb-icon">{item.icon}</span>
                {!col && <span className="sb-label">{item.label}</span>}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );

  const sidebarBody = (col = false) => (
    <div className="sb-inner">
      <div className="sb-logo">
        <div className="sb-logo-mark">
          <img src={BRAND_LOGO_URL} alt="" width={28} height={28} decoding="async" />
        </div>
        {!col && <span className="sb-logo-text">{BRAND_NAME}</span>}
        {!col && <span className="sb-logo-badge">v2</span>}
      </div>

      <div className="sb-scroll">{renderGroups(col)}</div>

      {user && (
        <div className="sb-footer">
          <Dropdown menu={{ items: userMenuItems }} placement="topRight" trigger={['click']}>
            <div
              className={`sb-user${col ? ' sb-user-collapsed' : ''}`}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  (e.currentTarget as HTMLElement).click();
                }
              }}
            >
              <div className="sb-user-av">
                {user.avatarUrl ? (
                  <img src={user.avatarUrl} alt="" className="sb-user-av-img" />
                ) : (
                  (user.fullName || user.email || 'U')[0].toUpperCase()
                )}
              </div>
              {!col && (
                <div className="sb-user-info">
                  <div className="sb-user-name">{user.fullName || user.email}</div>
                  <div className="sb-user-role">{user.role}</div>
                </div>
              )}
            </div>
          </Dropdown>
        </div>
      )}
    </div>
  );

  if (isMobile) {
    return (
      <Drawer
        placement="left"
        open={mobileOpen}
        onClose={onMobileClose}
        width={240}
        className="mobile-sidebar-drawer"
        closeIcon={<CloseOutlined />}
        styles={{ header: { display: 'none' }, body: { padding: 0 } }}
      >
        {sidebarBody(false)}
      </Drawer>
    );
  }

  return (
    <Sider collapsed={collapsed} className="sidebar" width={220} collapsedWidth={64} trigger={null}>
      {sidebarBody(collapsed)}
      <button
        className="sb-collapse-btn"
        onClick={() => setCollapsed(!collapsed)}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
      </button>
    </Sider>
  );
};

export default Sidebar;
